import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo, Suspense } from 'react';
import { getCurrentUser, logout, updateAnalyticsConsent } from '../services/authService';
import { hasModule, refreshEffectivePermissions } from '../utils/permissions';
import { useSessionCheck } from '../hooks/useSessionCheck';
import { useModal } from './useModal';
import FeedbackModal from './FeedbackModal';
import DocSuggestionModal from './DocSuggestionModal';
import CustomerSelectionModal from './CustomerSelectionModal';
import AnalyticsConsentModal from './AnalyticsConsentModal';
import apiService from '../services/apiService';
import dataService from '../services/dataService';
import { useAppSettings } from '../contexts/AppSettingsContext';
import { SyncProvider } from '../contexts/SyncContext';
import SyncStatusBar from './SyncStatusBar';
import SyncFooterButton from './SyncFooterButton';
import { applyAnalyticsConsent, isUmamiConfigured } from '../services/umamiService';
import { getDesign, loadAppearanceFromServer } from '../utils/appearance';
import ModernShell from './shell/ModernShell';
import { buildNavigation } from './shell/navigation';
import { OnboardingProvider } from './onboarding/OnboardingProvider';
import { Skeleton } from './ui';
import './Layout.css';

// Platzhalter, während eine Seite nachgeladen wird
function PageLoading() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 0' }} aria-busy="true" aria-label="Seite wird geladen">
      <Skeleton width="40%" height={24} />
      <Skeleton height={56} />
      <Skeleton height={56} />
      <Skeleton height={56} />
    </div>
  );
}

// Anzahl der News, die auf Freigabe warten (nur für News-Moderatoren)
function useNewsPendingCount(user) {
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!hasModule(user, 'manage-news')) return;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await apiService.get('/news/pending-count');
        if (!cancelled) setPendingCount(data.count || 0);
      } catch { /* silent */ }
    };
    load();
    const interval = setInterval(load, 60000); // refresh every minute
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);

  return pendingCount;
}

// Sub-component: News Feed nav link with pending-count badge for admins
function NewsFeedNavLink({ isActive, closeMobileMenu, user }) {
  const pendingCount = useNewsPendingCount(user);
  if (!user) return null;

  return (
    <Link
      to="/news-feed"
      className={`nav-link ${isActive('/news-feed') ? 'active' : ''}`}
      onClick={closeMobileMenu}
      style={{ position: 'relative' }}
    >
      📰 News Feed
      {pendingCount > 0 && (
        <span className="badge" style={{ marginLeft: '0.3rem' }}>{pendingCount}</span>
      )}
    </Link>
  );
}

export default function Layout() {
  const [user, setUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [customerSelection, setCustomerSelection] = useState({
    show: false,
    reason: 'no_selection',
    newCustomers: [],
    allCustomers: [],
    currentCustomerIds: []
  });
  const [appVersion, setAppVersion] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError, ModalComponent } = useModal();

  const [easterEggStage, setEasterEggStage] = useState(0);
  const [easterEggTimer, setEasterEggTimer] = useState(null);
  const { easyhelpLeaderboardEnabled, umamiEnabled, developerOptionsEnabled } = useAppSettings();

  const hasModulePerm = (moduleKey) => hasModule(user, moduleKey);

  // Neues Design (Standard) oder altes Design (wird nicht weiterentwickelt)
  const [design, setDesign] = useState(getDesign);
  // undefined = noch nicht geladen, null = Server nicht erreichbar
  const [onboardingFromServer, setOnboardingFromServer] = useState(undefined);
  const newsPendingCount = useNewsPendingCount(user);

  const navigation = useMemo(
    () => buildNavigation((moduleKey) => hasModule(user, moduleKey), {
      leaderboard: easyhelpLeaderboardEnabled
    }),
    [user, easyhelpLeaderboardEnabled]
  );

  const handleCopyrightClick = () => {
    if (!developerOptionsEnabled) return;
    setEasterEggStage(1);
    if (easterEggTimer) clearTimeout(easterEggTimer);
    const timer = setTimeout(() => setEasterEggStage(0), 3000);
    setEasterEggTimer(timer);
  };

  const handleBrandClick = () => {
    if (!developerOptionsEnabled || easterEggStage !== 1) return;
    setEasterEggStage(0);
    if (easterEggTimer) clearTimeout(easterEggTimer);

    // Unlock!
    localStorage.setItem('developerOptionsUnlocked', 'true');
    showSuccess('Entwicklermodus aktiviert', 'Die erweiterten Entwickleroptionen wurden auf diesem Gerät erfolgreich freigeschaltet.');
  };

  // Automatische Session-Prüfung
  useSessionCheck();

  useEffect(() => {
    loadUser();
    refreshEffectivePermissions();
    loadAppearanceFromServer().then((preferences) => {
      setOnboardingFromServer(preferences ? (preferences.onboarding || null) : null);
    });
    const handleAppearance = () => setDesign(getDesign());
    window.addEventListener('appearance-changed', handleAppearance);
    loadCartCount();
    loadVersion();
    checkCustomerSelection();

    const handleAuthChange = () => loadUser();
    window.addEventListener('auth-changed', handleAuthChange);

    // Online/Offline Events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Automatische Synchronisation alle 2 Stunden
    const syncInterval = setInterval(async () => {
      if (navigator.onLine) {
        console.log('🔄 Automatische 2-Stunden-Synchronisation gestartet...');
        try {
          const result = await dataService.checkAndSync(true);
          if (result.synced) {
            console.log('✅ Automatische Synchronisation erfolgreich');
          }
        } catch (error) {
          console.error('❌ Automatische Synchronisation fehlgeschlagen:', error);
        }
      } else {
        console.log('⚠️ Automatische Synchronisation übersprungen - Offline');
      }
    }, 2 * 60 * 60 * 1000); // 2 Stunden in Millisekunden

    return () => {
      window.removeEventListener('auth-changed', handleAuthChange);
      window.removeEventListener('appearance-changed', handleAppearance);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(syncInterval);
    };
  }, []);

  useEffect(() => {
    if (!user || !isUmamiConfigured()) return;
    applyAnalyticsConsent(user.analyticsConsent === true, umamiEnabled);
  }, [user?.analyticsConsent, umamiEnabled]);

  const showAnalyticsConsentModal =
    isUmamiConfigured() &&
    umamiEnabled &&
    !!user &&
    !user.analyticsConsentAsked &&
    !customerSelection.show;

  const handleAnalyticsConsent = async (consent) => {
    try {
      await updateAnalyticsConsent(consent);
      applyAnalyticsConsent(consent, umamiEnabled);
      await loadUser();
    } catch (err) {
      showError('Fehler', err.message || 'Einstellung konnte nicht gespeichert werden');
    }
  };

  const checkCustomerSelection = async () => {
    try {
      const status = await apiService.get('/user/customer-selection-status');
      if (status.needs_selection) {
        const myCustomers = await apiService.get('/user/my-customers');
        setCustomerSelection({
          show: true,
          reason: status.reason,
          newCustomers: status.new_customers || [],
          allCustomers: status.all_customers || [],
          currentCustomerIds: (myCustomers.customers || []).map(c => c.id)
        });
      }
    } catch (err) {
      console.error('Kundenauswahl-Check fehlgeschlagen:', err);
    }
  };

  const handleCustomerSelectionSave = async (selectedIds) => {
    await apiService.put('/user/my-customers', { customer_ids: selectedIds });
    setCustomerSelection(prev => ({ ...prev, show: false }));
  };

  const loadUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      console.log('👤 Geladener Benutzer:', {
        username: currentUser?.username,
        permissions: currentUser?.permissions,
        customPermissions: currentUser?.customPermissions,
        firstname: currentUser?.firstname,
        lastname: currentUser?.lastname
      });
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to load user:', error);
    }
  };

  const loadVersion = async () => {
    const localVersion = __APP_VERSION__;
    try {
      const versionData = await apiService.get('/version');
      setAppVersion(`${localVersion}+${versionData.buildDate || '00000000'}`);
    } catch (error) {
      console.error('Failed to load version:', error);
      setAppVersion(`${localVersion}+00000000`);
    }
  };

  const loadCartCount = async () => {
    try {
      const { getCart } = await import('../services/dbService');
      const cart = await getCart();
      setCartCount(cart.length);
    } catch (error) {
      console.error('Failed to load cart:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleFeedbackSubmit = async (feedbackType, message, images = []) => {
    try {
      const formData = new FormData();
      formData.append('feedbackType', feedbackType);
      formData.append('message', message);
      images.forEach((file) => formData.append('images', file));

      // Silent collection of debug info
      try {
        const { collectDebugInfo } = await import('../utils/debugCollector');
        const debugInfo = await collectDebugInfo();
        formData.append('debugInfo', JSON.stringify(debugInfo));
      } catch (debugErr) {
        console.warn('Failed to collect debug info for feedback:', debugErr);
      }

      await apiService.postFormData('/feedback/send', formData);
      setShowFeedbackModal(false);
      showSuccess('Feedback gesendet', 'Vielen Dank für dein Feedback!');
    } catch (error) {
      showError('Fehler', error.message || 'Feedback konnte nicht gesendet werden');
      throw error;
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const isDevHost = window.location.hostname.includes('docker');
  const outletContext = { refreshCartCount: loadCartCount, navigation, isOnline };

  const sharedDialogs = (
    <>
          <CustomerSelectionModal
            isOpen={customerSelection.show}
            reason={customerSelection.reason}
            newCustomers={customerSelection.newCustomers}
            allCustomers={customerSelection.allCustomers}
            currentCustomerIds={customerSelection.currentCustomerIds}
            onSave={handleCustomerSelectionSave}
          />
          <AnalyticsConsentModal
            isOpen={showAnalyticsConsentModal}
            onAccept={() => handleAnalyticsConsent(true)}
            onDecline={() => handleAnalyticsConsent(false)}
          />
      <ModalComponent />
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        onSubmit={handleFeedbackSubmit}
      />
      <DocSuggestionModal
        isOpen={showSuggestionModal}
        onClose={() => setShowSuggestionModal(false)}
      />
    </>
  );

  if (design !== 'classic') {
    return (
      <SyncProvider>
        <OnboardingProvider
          user={user}
          navigation={navigation}
          initialOnboarding={user ? onboardingFromServer : undefined}
          autoStart
          blocked={customerSelection.show || showAnalyticsConsentModal}
        >
          <ModernShell
            user={user}
            navigation={navigation}
            isOnline={isOnline}
            badges={{ '/cart': cartCount, '/news-feed': newsPendingCount }}
            appVersion={appVersion}
            statusBar={<SyncStatusBar />}
            devNotice={isDevHost && (
              <div className="dev-notice dev-notice--modern">
                <span className="dev-icon">⚠️</span>
                <span className="dev-text"><strong>DEV-Version:</strong> Daten werden nicht in die Release-Version übernommen, Feedback und E-Mail-Versand sind nicht aktiv.</span>
              </div>
            )}
            footerExtra={<SyncFooterButton />}
            onLogout={handleLogout}
            onFeedback={() => setShowFeedbackModal(true)}
            onSuggestion={() => setShowSuggestionModal(true)}
            onCopyrightClick={handleCopyrightClick}
            onBrandClick={handleBrandClick}
          >
            <Suspense fallback={<PageLoading />}>
              <Outlet context={outletContext} />
            </Suspense>
          </ModernShell>
          {sharedDialogs}
        </OnboardingProvider>
      </SyncProvider>
    );
  }

  // Altes Design – unverändert, wird nicht weiterentwickelt
  return (
    <SyncProvider>
      <OnboardingProvider user={user} navigation={navigation} initialOnboarding={undefined} autoStart={false}>
      <div className="layout">
        {sharedDialogs}
        <header className="layout-header-sticky">
          <SyncStatusBar />
          <div className="layout-header">
            <div className="header-content">
              <div className="header-left">
                <Link to="/" className="app-logo">
                  🔧
                </Link>
                <div className="header-brand">
                  <span className="header-app-title">Material-App</span>
                  <span className="header-app-sub">Ersatzteillogistik</span>
                </div>
                <span className={`status-badge ${isOnline ? 'online' : 'offline'}`}>
                  {isOnline ? '🟢 Online' : '🔴 Offline'}
                </span>
                {window.location.hostname.includes('docker') && (
                  <div className="dev-notice">
                    <span className="dev-icon">⚠️</span>
                    <span className="dev-text">
                      <strong>DEV-Version:</strong> Dies ist eine Entwicklungsumgebung. Eingegebene Daten werden <strong>nicht</strong> in die Release-Version übernommen. Feedback-Funktionen und der E-Mail-Versand sind in dieser Umgebung <strong>nicht aktiv</strong>.
                    </span>
                  </div>
                )}
              </div>

              <button className="mobile-menu-btn" onClick={toggleMobileMenu}>
                <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>

              {mobileMenuOpen && (
                <div className="menu-overlay" onClick={closeMobileMenu}></div>
              )}

              <nav className={`header-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
                <div className="nav-header-brand">
                  <div className="nav-header-brand-info">
                    <span className="nav-header-icon">🔧</span>
                    <div>
                      <span className="nav-header-title">Material-App</span>
                      <span className="nav-header-sub">Ersatzteillogistik</span>
                    </div>
                  </div>
                  <button className="menu-close-btn" onClick={closeMobileMenu}>
                    <span className="close-icon">✕</span>
                  </button>
                </div>

                <div className="nav-section-label">Navigation</div>
                <Link
                  to="/search"
                  className={`nav-link ${isActive('/search') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  🔍 Suche
                </Link>
                <Link
                  to="/documentation"
                  className={`nav-link ${isActive('/documentation') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  📚 Dokumentation
                </Link>
                <NewsFeedNavLink isActive={isActive} closeMobileMenu={closeMobileMenu} user={user} />

                {(hasModulePerm('cart')) && (
                  <Link
                    to="/cart"
                    className={`nav-link ${isActive('/cart') ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    🛒 Warenkorb {cartCount > 0 && <span className="badge">{cartCount}</span>}
                  </Link>
                )}
                {hasModulePerm('easyhelp') && (
                  <Link
                    to="/easyhelp"
                    className={`nav-link ${isActive('/easyhelp') ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    🆘 Easyhelp
                  </Link>
                )}
                {easyhelpLeaderboardEnabled && hasModulePerm('easyhelp') && (
                  <Link
                    to="/leaderboard"
                    className={`nav-link ${isActive('/leaderboard') ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    🏆 Leaderboard
                  </Link>
                )}
                {(hasModulePerm('dispatcher-orders')) && (
                  <Link
                    to="/dispatcher-orders"
                    className={`nav-link ${isActive('/dispatcher-orders') ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📋 Disponenten-Bestellung
                  </Link>
                )}
                {(hasModulePerm('passwords')) && (
                  <Link
                    to="/passwords"
                    className={`nav-link ${isActive('/passwords') ? 'active' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    🔐 Passwort-Datenbank
                  </Link>
                )}

                {/* Trennlinie für Verwaltungsbereich */}
                {(hasModulePerm('manage-devices') || hasModulePerm('manage-parts') || hasModulePerm('maintenance') || hasModulePerm('doc-generator') || hasModulePerm('manage-customers') || hasModulePerm('manage-categories') || hasModulePerm('manage-doc-templates') || hasModulePerm('shipping-methods') || hasModulePerm('shipping-storage') || hasModulePerm('permissions-management') || hasModulePerm('document_archive') || hasModulePerm('analytics') || hasModulePerm('manage-users')) && (
                  <div className="nav-section-label">Verwaltung</div>
                )}

                {(hasModulePerm('manage-devices')) && (
                  <Link
                    to="/manage-devices"
                    className={`nav-link ${isActive('/manage-devices') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📱 Geräte-Verwaltung
                  </Link>
                )}
                {(hasModulePerm('manage-parts')) && (
                  <Link
                    to="/manage-parts"
                    className={`nav-link ${isActive('/manage-parts') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    🔧 Teile-Verwaltung
                  </Link>
                )}
                {(hasModulePerm('maintenance')) && (
                  <Link
                    to="/maintenance"
                    className={`nav-link ${isActive('/maintenance') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    🧰 Wartung
                  </Link>
                )}
                {(hasModulePerm('doc-generator')) && (
                  <Link
                    to="/doc-generator"
                    className={`nav-link ${isActive('/doc-generator') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📝 Dokumentations Management
                  </Link>
                )}
                {(hasModulePerm('manage-customers')) && (
                  <Link
                    to="/manage-customers"
                    className={`nav-link ${isActive('/manage-customers') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    🏢 Kunden-Verwaltung
                  </Link>
                )}
                {(hasModulePerm('manage-categories')) && (
                  <Link
                    to="/manage-categories"
                    className={`nav-link ${isActive('/manage-categories') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📁 Kategorie-Verwaltung
                  </Link>
                )}
                {(hasModulePerm('manage-doc-templates')) && (
                  <Link
                    to="/manage-doc-templates"
                    className={`nav-link ${isActive('/manage-doc-templates') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📄 Word-Vorlagen
                  </Link>
                )}
                {(hasModulePerm('shipping-methods')) && (
                  <Link
                    to="/shipping-methods"
                    className={`nav-link ${isActive('/shipping-methods') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📦 Versandwege
                  </Link>
                )}
                {(hasModulePerm('shipping-storage')) && (
                  <Link
                    to="/shipping-storage"
                    className={`nav-link ${isActive('/shipping-storage') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📥 Sammelkörbe
                  </Link>
                )}
                {(hasModulePerm('manage-users')) && (
                  <Link
                    to="/manage-users"
                    className={`nav-link ${isActive('/manage-users') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    👥 Benutzer-Verwaltung
                  </Link>
                )}
                {(hasModulePerm('permissions-management')) && (
                  <Link
                    to="/permissions"
                    className={`nav-link ${isActive('/permissions') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    🛡️ Berechtigungen
                  </Link>
                )}
                {(hasModulePerm('analytics')) && (
                  <Link
                    to="/analytics"
                    className={`nav-link ${isActive('/analytics') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📊 Activity Log & Analytics
                  </Link>
                )}
                {(hasModulePerm('document_archive')) && (
                  <Link
                    to="/document-archive"
                    className={`nav-link ${isActive('/document-archive') ? 'active' : ''} ${!isOnline ? 'disabled' : ''}`}
                    onClick={closeMobileMenu}
                  >
                    📁 Dokumenten-Archiv
                  </Link>
                )}

                <div className="nav-section-label">Einstellungen &amp; Feedback</div>

                <Link
                  to="/settings"
                  className={`nav-link ${isActive('/settings') ? 'active' : ''}`}
                  onClick={closeMobileMenu}
                >
                  ⚙️ Einstellungen
                </Link>
                <button
                  className="nav-link feedback-link"
                  onClick={() => { closeMobileMenu(); setShowFeedbackModal(true); }}
                >
                  💬 Feedback
                </button>
                <button
                  className="nav-link feedback-link"
                  onClick={() => { closeMobileMenu(); setShowSuggestionModal(true); }}
                >
                  💡 Doku-Vorschlag
                </button>

                {user && (
                  <div className="mobile-user-section">
                    <div className="mobile-user-profile">
                      <div className="user-avatar">
                        {user.firstname ? user.firstname.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                      </div>
                      <span className="user-name">
                        {user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : user.username}
                      </span>
                    </div>
                    <button onClick={() => { closeMobileMenu(); handleLogout(); }} className="mobile-logout-btn">
                      <span className="logout-icon">🚪</span>
                      Abmelden
                    </button>
                    {appVersion && (
                      <div className="mobile-version-display">
                        <span className="version-label">Version:</span>
                        <span className="version-number">{appVersion}</span>
                      </div>
                    )}
                  </div>
                )}
              </nav>
            </div>
          </div>
        </header>

        <div className="layout-content-wrapper">
          <main className="layout-main">
            <Suspense fallback={<PageLoading />}>
              <Outlet context={{ refreshCartCount: loadCartCount, navigation, isOnline }} />
            </Suspense>
          </main>


          <footer className="layout-footer">
            <div className="footer-content">
              <div className="footer-brand-col">
                <span className="footer-logo">🔧</span>
                <div>
                  <h3 className="footer-brand-title" onClick={handleBrandClick} style={{ cursor: developerOptionsEnabled && easterEggStage === 1 ? 'pointer' : 'inherit' }}>Material-App</h3>
                  <p className="footer-brand-sub">Ersatzteillogistik &amp; Dokumentation</p>
                </div>
              </div>
              <div className="footer-links-col">
                <h4 className="footer-col-title">Rechtliches</h4>
                <a href="https://bentomax.de/impressum" target="_blank" rel="noopener noreferrer">Impressum</a>
                <Link to="/datenschutz">Datenschutz</Link>
              </div>
              <div className="footer-info-col">
                <h4 className="footer-col-title">Info</h4>
                <p className="footer-dev-credit">
                  Entwickelt mit ❤️ von{' '}
                  <a href="https://lars-jaensch.de" target="_blank" rel="noopener noreferrer">Lars Jänsch</a>
                </p>
                {appVersion && <p className="footer-version">Version: v{appVersion}</p>}
              </div>
            </div>
            <div className="footer-bottom">
              <SyncFooterButton />
              <p>
                <span onClick={handleCopyrightClick} style={{ cursor: developerOptionsEnabled ? 'pointer' : 'inherit' }}>©</span>
                {' '}{new Date().getFullYear()} Lars Jänsch · Alle Rechte vorbehalten
              </p>
            </div>
          </footer>
        </div>
      </div>
      </OnboardingProvider>
    </SyncProvider>
  );
}
